import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { GroupBy } from 'src/app/core/_enums/groupby';
import { EOperationProofType, ProofTypeLabelKeys } from 'src/app/core/_enums/proofTypes.code';
import { SiteMapUrl } from 'src/app/core/_enums/sitemap.url';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { GlobalConstants } from 'src/constants/global.constants';
import { CeuValue } from 'src/app/core/_models/ceuValues';
import { DetailedOperation } from 'src/app/core/_models/detailedOperation';
import { Energie } from 'src/app/core/_models/energie';
import { EnginOperation } from 'src/app/core/_models/enginOperation';
import { Indicator } from 'src/app/core/_models/indicator';
import { DredgingProjectUsageResourceModel, Operation, TunnelingProjectUsageResourceModel } from 'src/app/core/_models/operation';
import { OperationProof, OperationProofWithFile } from 'src/app/core/_models/operationProof';
import { Period } from 'src/app/core/_models/period';
import { ProductInOut } from 'src/app/core/_models/productInOut';
import { ProductOperation } from 'src/app/core/_models/productOperation';
import { Transport } from 'src/app/core/_models/transport';
import { Vehicle } from 'src/app/core/_models/vehicle';
import { AcheminementService } from 'src/app/core/_services/acheminement.service';
import { AdministrationService } from 'src/app/core/_services/administration.service';
import { EnergieService } from 'src/app/core/_services/energie.service';
import { EnginAtelierService } from 'src/app/core/_services/engin.atelier.service';
import { OperationService } from 'src/app/core/_services/operation.service';
import { ProductFormulaService } from 'src/app/core/_services/product.formula.service';
import { ProjectService } from 'src/app/core/_services/project.service';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { SpinnerOverlayService } from 'src/app/core/_services/spinner.overlay.service';
import { TransportService } from 'src/app/core/_services/transport.service';
import { UploadService } from 'src/app/core/_services/upload.service';
import { environment } from 'src/environments/environment';
import { ProjectType } from '../../core/_models/projectType';
import { ProjectTypeService } from '../../core/_services/projectTypes.service';
import { DialogAddCompositionComponent } from '../shared/dialog-add-composition/dialog-add-composition.component';
import { DialogChangeEnginComponent } from '../shared/dialog-change-engin/dialog-change-engin.component';
import { DialogDredgingEquipmentComponent, DialogDredgingEquipmentComponentResponseDataModel } from '../shared/dialog-dredging-equipment/dialog-dredging-equipment.component';
import { DialogEditEnginComponent } from '../shared/dialog-edit-engin/dialog-edit-engin.component';
import { DialogEditOperationComponent } from '../shared/dialog-edit-operation/dialog-edit-operation.component';
import { DialogEditProofComponent } from '../shared/dialog-edit-proof/dialog-edit-proof.component';
import { DialogJustificativeComponent } from '../shared/dialog-justificative/dialog-justificative.component';
import { DialogRenameCopyComponent } from '../shared/dialog-rename-copy/dialog-rename-copy.component';
import { DialogTunnelingEquipmentComponent, DialogTunnelingEquipmentComponentResponseDataModel } from '../shared/dialog-tunneling-equipment/dialog-tunneling-equipment.component';
import { DialogUploadJustificativesComponent } from '../shared/dialog-upload-justificatives/dialog-upload-justificatives.component';
import { TableColumn, TableRow } from '../shared/table/table.component';
import { LabelI18nService } from 'src/app/core/_services/label-i18n.service';

export enum ResourceType {
  Tunnelier = 'tunnelier',
  Dredge = 'dredge'
}

interface OperationProofItem extends OperationProof {
  resourceName: string;
  resourceType?: 'input' | 'output' | 'vehicle';
  resourceId?: string;
  syntheticKind?: 'dredge' | 'tunnel';
  syntheticItem?: DredgingProjectUsageResourceModel | TunnelingProjectUsageResourceModel;
}

@Component({
  selector: 'app-solution-operation',
  templateUrl: './solution-operation.component.html',
  styleUrls: ['./solution-operation.component.scss']
})
export class SolutionOperationComponent implements OnInit, OnChanges {
  @ViewChild('panelRef') panelRef!: MatExpansionPanel;
  private _operation!: Operation;
  @Input()
  set operation(value: Operation) {
    const wasJustOpened = !this._operation && this.panelRef?.expanded;
    this._operation = value;
    this.refreshResourceRows();
    this.resolveAllAtelierEnergies();
    this.resolveAllTunnelerEnergies();
    if (this.panelRef?.expanded || wasJustOpened) {
      this.loadProofsForOperation(this._operation);
    }
  }
  @Input() isEditable: boolean = false;
  @Input() activeIndicator!: Indicator;
  @Input() allIndicators: Indicator[] = [];
  @Input() wsAppPath!: string;
  @Input() isPeriodModeEnabled: boolean = false;
  @Input() allPeriods: Period[] = [];
  @Input() versionId?: string;

  @Input() periodAssignments: { [periodId: string]: string } = {};
  @Input() copyRootName: string = '';

  @Output() operationUpdated = new EventEmitter<Operation>();
  @Output() periodsChanged = new EventEmitter<{ operationId: string; added: string[]; removed: string[] }>();

  private isPeriodMenuOpen = false;
  private tickedPeriods = new Set<string>();
  private assignedPeriodsOnOpen = new Set<string>();
  @Output() operationDeleteRequested = new EventEmitter<Operation>();

  get operation(): Operation {
    return this._operation;
  }

  SiteMapUrl = SiteMapUrl;
  EMPTY_GUID = GlobalConstants.EMPTY_GUID;

  proofFilterForm: FormGroup;
  unfilteredProofs: OperationProofItem[] = [];
  filteredProofs: OperationProofItem[] = [];
  inputProductRows: TableRow[] = [];
  outputProductRows: TableRow[] = [];
  vehicleRows: TableRow[] = [];
  proofRows: TableRow[] = [];
  availableResources: string[] = [];
  availableProofTypes: string[] = [];
  allTransports: Transport[] = [];
  allEnergies: Energie[] = [];
  vehicleEnergies: Energie[] = [];
  isLoadingProducts = false;
  commentCharLimit = 80;
  expandedComments = new Set<string>();
  displayAsDay: boolean = true;
  hoursForDay?: number;
  operationTypes: ProjectType[] = [];
  productColumns: TableColumn[] = [];
  vehicleColumns: TableColumn[] = [];
  proofColumns: TableColumn[] = [];

  private productPanelStates = new Map<string, boolean>();
  private vehiclePanelStates = new Map<string, boolean>();

  isDragging = false;

  constructor(
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    private operationService: OperationService,
    private projectTypeService: ProjectTypeService,
    private spinnerService: SpinnerOverlayService,
    private productFormulaService: ProductFormulaService,
    private projectService: ProjectService,
    private enginService: EnginAtelierService,
    private transportService: TransportService,
    private energieService: EnergieService,
    private acheminementService: AcheminementService,
    private administrationService: AdministrationService,
    private uploadService: UploadService,
    private fb: FormBuilder,
    private translate: TranslateService,
    private labelI18n: LabelI18nService
  ) {
    this.proofFilterForm = this.fb.group({
      fileNameSearch: [''],
      resourceNameFilter: [''],
      typesFilter: [[]]
    });
  }

  ngOnInit(): void {
    this.initializeColumns();

    this.proofFilterForm.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
      this.applyFilters();
    });

    this.loadTransportsForVersion();
    this.energieService.getAllForTransportation().subscribe((data) => {
      this.allEnergies = data;
      this.resolveAllAtelierEnergies();
    });
    this.energieService.getAllForVehicle().subscribe((data) => {
      this.vehicleEnergies = data;
      this.resolveAllAtelierEnergies();
      this.resolveAllTunnelerEnergies();
    });

    this.administrationService.get().subscribe((data) => {
      this.hoursForDay = data.dailyUsageTime;
    });

    this.projectTypeService.getAll().subscribe((types) => {
      this.operationTypes = types;
    });
  }

  private enginsMasterById?: Promise<{ [id: string]: any }>;

  private isBlankId(id?: string): boolean {
    return !id || id === GlobalConstants.EMPTY_GUID;
  }

  private loadEnginsMasterIndex(): Promise<{ [id: string]: any }> {
    if (this.enginsMasterById) return this.enginsMasterById;
    this.enginsMasterById = this.enginService
      .getOnlyEngins(GroupBy.family)
      .toPromise()
      .then((tree) => {
        const map: { [id: string]: any } = {};
        const visit = (node: any) => {
          if (Array.isArray(node)) return node.forEach(visit);
          if (!node || typeof node !== 'object') return;
          if (node.resourceId) map[node.resourceId] = node;
          Object.values(node).forEach(visit);
        };
        visit(tree);
        return map;
      });
    return this.enginsMasterById;
  }

  private async resolveAtelierEnginEnergies(atelierEngins: any[] | undefined): Promise<void> {
    if (!atelierEngins?.length) return;
    if (!this.vehicleEnergies?.length && !this.allEnergies?.length) return;
    const enginsById = await this.loadEnginsMasterIndex();
    const allEnergies = [...(this.vehicleEnergies || []), ...(this.allEnergies || [])];
    atelierEngins.forEach((engin) => {
      const enginId = !this.isBlankId(engin.resourceId) ? engin.resourceId : engin.childResource;
      const canonical = enginsById[enginId];
      if (!canonical?.ruade) engin.energyId = canonical?.energyId;
      engin.energyName = this.isBlankId(engin.energyId) ? undefined : allEnergies.find((e) => e.resourceId === engin.energyId)?.displayName;
    });
  }

  private resolveAllAtelierEnergies(): void {
    const vehicles = this._operation?.detailedOperation?.vehicles;
    if (!vehicles?.length) return;
    vehicles.forEach((v) => {
      if (v.isAtelier) this.resolveAtelierEnginEnergies(v.atelierEngins);
    });
  }

  private resolveAllTunnelerEnergies(): void {
    const tunnelers = this._operation?.detailedOperation?.tunneling;
    if (!tunnelers?.length) return;
    tunnelers.forEach((t) => {
      t.energyName = this.resolveTunnelerEnergyName(t.selectedFuel);
    });
  }

  updateTunnelerFuel(item: TunnelingProjectUsageResourceModel, operation: Operation, energyId: string): void {
    if (!this.isEditable || !operation.id) return;
    item.selectedFuel = energyId === GlobalConstants.EMPTY_GUID ? undefined : energyId;
    item.energyName = this.resolveTunnelerEnergyName(item.selectedFuel);
    this.persistOperation(operation, this.translate.instant('success_tunneling_equipment_updated'));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['versionId'] && !changes['versionId'].firstChange) {
      this.loadTransportsForVersion();
    }
  }

  private loadTransportsForVersion(): void {
    this.transportService.getAll(undefined, this.versionId).subscribe((data) => (this.allTransports = data));
  }

  private initializeColumns(): void {
    this.productColumns = [
      { key: 'status', label: '', cssClass: 'col-status fit-content', customTemplate: true, sortable: false },
      { key: 'product', label: this.translate.instant('table_col_product'), cssClass: 'col-name', customTemplate: true, sortable: false },
      { key: 'transport', label: this.translate.instant('table_col_transport'), cssClass: 'col-transport', customTemplate: true, sortable: false },
      { key: 'energy', label: this.translate.instant('table_col_energy'), cssClass: 'col-energy', customTemplate: true, sortable: false },
      { key: 'multimodal', label: this.translate.instant('table_col_multimodal'), cssClass: 'col-icon text-center', customTemplate: true, sortable: false, align: 'center' },
      { key: 'distance', label: this.translate.instant('table_col_distance'), cssClass: 'col-val text-center', customTemplate: true, sortable: false, align: 'center' },
      { key: 'doubleFreight', label: this.translate.instant('table_col_double_freight'), cssClass: 'col-icon text-center', customTemplate: true, sortable: false, align: 'center' },
      { key: 'unit', label: this.translate.instant('table_col_work_unit'), cssClass: 'col-uo', customTemplate: true, sortable: false },
      { key: 'quantity', label: this.translate.instant('table_col_real_quantity'), cssClass: 'col-val', customTemplate: true, sortable: false },
      { key: 'proofs', label: this.translate.instant('table_col_proofs'), cssClass: 'col-pj text-center', customTemplate: true, sortable: false, align: 'center' },
      { key: 'actions', label: '', cssClass: 'col-actions text-center', customTemplate: true, sortable: false }
    ];
    this.vehicleColumns = [
      { key: 'status', label: '', cssClass: 'col-status fit-content', customTemplate: true, sortable: false },
      { key: 'vehicle', label: this.translate.instant('table_col_engine'), cssClass: 'col-name-lg', customTemplate: true, sortable: false },
      { key: 'energy', label: this.translate.instant('table_col_energy'), cssClass: 'col-energy', customTemplate: true, sortable: false },
      { key: 'quantity', label: this.translate.instant('table_col_quantity'), cssClass: 'col-val text-center', customTemplate: true, sortable: false, customHeaderTemplate: true, align: 'center' },
      { key: 'proofs', label: this.translate.instant('table_col_proofs'), cssClass: 'col-pj text-center', customTemplate: true, sortable: false, align: 'center' },
      { key: 'actions', label: '', cssClass: 'col-actions text-center', customTemplate: true, sortable: false }
    ];
    this.proofColumns = [
      { key: 'resourceName', label: this.translate.instant('table_col_resource'), cssClass: 'col-product', sortable: false },
      { key: 'fileName', label: this.translate.instant('table_col_proof_name'), cssClass: 'col-file-name', customTemplate: true, sortable: false },
      { key: 'typesDisplay', label: this.translate.instant('table_col_proof_types'), cssClass: 'col-types', sortable: false },
      { key: 'comment', label: this.translate.instant('table_col_comment'), cssClass: 'col-comment', customTemplate: true, sortable: false },
      { key: 'actions', label: '', cssClass: 'col-actions text-center', customTemplate: true, sortable: false }
    ];
  }

  getOperationValue(operation: Operation): number {
    if (!this.activeIndicator) return 0;
    const total = operation.detailedOperation?.ceuValues?.find((x: CeuValue) => x.code === this.activeIndicator!.code && x.subIndicatorCode == 'TotalCost')?.ceuValue;
    return total || 0;
  }

  getFormattedOperationValue(operation: Operation): string {
    const total = this.getOperationValue(operation);

    if (!this.activeIndicator) {
      return '0';
    }

    if (this.activeIndicator.code === 'CO') {
      return total.toLocaleString(this.labelI18n.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    return this.labelI18n.exponential(total, 2);
  }

  getOperationTypeDisplayName(typeCode?: string): string {
    if (!typeCode || !this.operationTypes || this.operationTypes.length === 0) {
      return typeCode || '-';
    }
    const foundType = this.operationTypes.find((t) => t.code === typeCode);
    return foundType?.displayName || typeCode;
  }

  changePeriod(newPeriodId?: string): void {
    if (this.operation.periodId !== newPeriodId) {
      this.operationUpdated.emit({
        ...this.operation,
        periodId: newPeriodId
      });
    }
  }

  // Seule une copie se supprime : une opération d'origine est le reflet de la solution initiale.
  get isCopy(): boolean {
    return !!this.operation.copiedFromOperationId;
  }

  // Une copie naît avec les quantités du prévu : tant que personne n'a touché à ses lignes, le total de
  // la solution annonce le prévu et non le réalisé. Le repère disparaît à la première saisie.
  get needsAdjustment(): boolean {
    return this.isCopy && !this.operation.adjustedDate;
  }

  requestDelete(): void {
    this.operationDeleteRequested.emit(this.operation);
  }

  get canRenameCopy(): boolean {
    return this.isEditable && this.isCopy && !!this.copyRootName;
  }

  // Seul le suffixe se renomme : la racine reste celle de l'opération mère, sinon deux copies d'une
  // même opération ne se rapprochent plus. Elle est réimposée ici, y compris si la mère a été
  // renommée depuis la copie.
  renameCopy(): void {
    if (!this.canRenameCopy || !this.operation.id) return;

    const currentName = this.operation.displayName || '';
    const dialogRef = this.dialog.open(DialogRenameCopyComponent, {
      data: {
        rootName: this.copyRootName,
        suffix: currentName.startsWith(this.copyRootName) ? currentName.slice(this.copyRootName.length) : ''
      }
    });

    dialogRef.afterClosed().subscribe((displayName: string) => {
      if (!displayName || displayName === currentName) return;

      this.spinnerService.show();
      this.operationService.update(this.operation.id!, { ...this.operation, displayName }).subscribe({
        next: () => this.refetchAndUpdateOperation(this.operation.id!, this.translate.instant('success_operation_renamed')),
        error: (err) => this.handleApiError(err)
      });
    });
  }

  isAssignedToPeriod(period: Period): boolean {
    return !!this.periodAssignments[period.temporalityId];
  }

  // Les cases se cochent sans rien déclencher : tout est appliqué à la fermeture du menu, sinon la
  // première case déplacerait l'opération, son panneau disparaîtrait et le menu se refermerait.
  onPeriodMenuOpened(): void {
    this.assignedPeriodsOnOpen = new Set(Object.keys(this.periodAssignments));
    this.tickedPeriods = new Set(this.assignedPeriodsOnOpen);
    this.isPeriodMenuOpen = true;
  }

  onPeriodMenuClosed(): void {
    this.isPeriodMenuOpen = false;
    const added = [...this.tickedPeriods].filter((id) => !this.assignedPeriodsOnOpen.has(id));
    const removed = [...this.assignedPeriodsOnOpen].filter((id) => !this.tickedPeriods.has(id));

    if (added.length > 0 || removed.length > 0) {
      this.periodsChanged.emit({ operationId: this.operation.id!, added, removed });
    }
  }

  isPeriodTicked(period: Period): boolean {
    return this.isPeriodMenuOpen ? this.tickedPeriods.has(period.temporalityId) : this.isAssignedToPeriod(period);
  }

  togglePeriod(period: Period, checked: boolean): void {
    if (checked) {
      this.tickedPeriods.add(period.temporalityId);
    } else {
      this.tickedPeriods.delete(period.temporalityId);
    }
  }

  private refetchAndUpdateOperation(operationId: string, successMessage?: string): void {
    this.operationService.getById(operationId).subscribe({
      next: (refreshedOperation: Operation) => {
        if (successMessage) {
          this.snackbarService.open(SnackbarTypes.add, successMessage);
        }
        this.operation = {
          ...refreshedOperation,
          periodId: this.operation.periodId
        };
        this.operationUpdated.emit(this.operation);
        this.spinnerService.hide();
      },
      error: (err) => {
        console.error('API Error:', err);
        this.spinnerService.hide();
        this.snackbarService.showMessage(this.translate.instant('error_action_success_update_failed'));
      }
    });
  }

  changeProduct(item: ProductOperation, operation: Operation, type: 'input' | 'output'): void {
    if (!this.isEditable || !operation.id || this.isLoadingProducts) return;
    this.isLoadingProducts = true;
    this.spinnerService.show();

    if (type === 'input') {
      const operationType = operation.type || undefined;
      this.productFormulaService.getAll(GroupBy.family, operationType, this.versionId).subscribe(
        (data) => {
          this.productFormulaService.getAll(GroupBy.favorite, operationType).subscribe(
            (favoriteData) => {
              this.spinnerService.hide();
              const dialogRef = this.dialog.open(DialogAddCompositionComponent, {
                data: {
                  fullTree: data,
                  favoriteData: favoriteData,
                  isSingular: true,
                  editMode: true
                }
              });

              dialogRef.afterClosed().subscribe((selectedProducts: any[]) => {
                const newProduct = selectedProducts?.[0];
                if (newProduct) {
                  this.spinnerService.show();
                  const originalProductId = item.productId!;

                  item.productId = newProduct.resourceId;
                  item.displayName = newProduct.displayName;
                  item.productCode = newProduct.code;
                  item.workUnit = newProduct.workUnit;
                  item.productIsDeleted = false;

                  this.operationService.updateInboundProduct(operation.id!, originalProductId, item).subscribe({
                    next: (updatedOperation) => this.refetchAndUpdateOperation(operation.id!, this.translate.instant('success_product_updated')),
                    error: (err) => this.handleApiError(err)
                  });
                }
                this.isLoadingProducts = false;
              });
            },
            (err) => {
              this.handleApiError(err);
              this.isLoadingProducts = false;
            }
          );
        },
        (err) => {
          this.handleApiError(err);
          this.isLoadingProducts = false;
        }
      );
    } else {
      this.operationService.getProduitsSortants(operation.type).subscribe(
        (data) => {
          this.spinnerService.hide();
          const dialogRef = this.dialog.open(DialogAddCompositionComponent, {
            data: { fullTree: data, isSingular: true, editMode: true }
          });

          dialogRef.afterClosed().subscribe((selectedProducts: any[]) => {
            const newProduct = selectedProducts?.[0];
            if (newProduct) {
              this.spinnerService.show();
              const originalProductId = item.productId!;

              item.productId = newProduct.resourceId;
              item.displayName = newProduct.displayName;
              item.productCode = newProduct.code;
              item.workUnit = newProduct.workUnit;
              item.productIsDeleted = false;

              this.operationService.updateOutboundProduct(operation.id!, originalProductId, item).subscribe({
                next: (updatedOperation) => this.refetchAndUpdateOperation(operation.id!, this.translate.instant('success_product_updated')),
                error: (err) => this.handleApiError(err)
              });
            }
            this.isLoadingProducts = false;
          });
        },
        (err) => {
          this.handleApiError(err);
          this.isLoadingProducts = false;
        }
      );
    }
  }

  editProductDetails(item: ProductOperation, operation: Operation, type: 'input' | 'output'): void {
    if (!this.isEditable || !operation.id) return;

    const dialogRef = this.dialog.open(DialogEditOperationComponent, {
      data: {
        produit: { ...item },
        transports: this.allTransports,
        energies: this.allEnergies
      }
    });

    dialogRef.afterClosed().subscribe((updatedProductData) => {
      if (updatedProductData) {
        this.spinnerService.show();

        Object.assign(item, updatedProductData);

        const updateCall$ = type === 'input' ? this.operationService.updateInboundProduct(operation.id!, item.productId!, item) : this.operationService.updateOutboundProduct(operation.id!, item.productId!, item);

        updateCall$.subscribe({
          next: (updatedOperation) => this.refetchAndUpdateOperation(operation.id!, this.translate.instant('success_product_updated')),
          error: (err) => this.handleApiError(err)
        });
      }
    });
  }

  editProduct(item: ProductOperation, type: 'input' | 'output', operation: Operation): void {
    if (!this.isEditable || !operation.id) {
      return;
    }

    const dialogRef = this.dialog.open(DialogEditOperationComponent, {
      width: '400px',
      data: {
        produit: { ...item },
        transports: this.allTransports,
        energies: this.allEnergies
      }
    });

    dialogRef.afterClosed().subscribe((updatedProductData) => {
      if (updatedProductData) {
        this.spinnerService.show();

        Object.assign(item, updatedProductData);

        const updateCall$ = type === 'input' ? this.operationService.updateInboundProduct(operation.id!, item.productId!, item) : this.operationService.updateOutboundProduct(operation.id!, item.productId!, item);

        updateCall$.subscribe({
          next: (updatedOperation) => this.refetchAndUpdateOperation(operation.id!, this.translate.instant('success_product_updated')),
          error: (err) => this.handleApiError(err)
        });
      }
    });
  }

  changeVehicle(item: Vehicle, operation: Operation): void {
    if (!this.isEditable || !operation.id || this.isLoadingProducts) return;
    this.isLoadingProducts = true;
    this.spinnerService.show();

    this.enginService.getAll(undefined, operation.type).subscribe(
      (allEngins) => {
        this.enginService.getAll(GroupBy.favoriteDialog, operation.type).subscribe(
          (favoriteEngins) => {
            this.spinnerService.hide();

            const dialogRef = this.dialog.open(DialogChangeEnginComponent, {
              data: {
                fullTree: allEngins,
                favoriteData: favoriteEngins
              }
            });

            dialogRef.afterClosed().subscribe((dataResult) => {
              if (dataResult && dataResult.newEngin) {
                this.spinnerService.show();
                const originalVehicleId = item.vehicleId!;

                item.vehicleId = dataResult.newEngin.resourceId;
                item.displayName = dataResult.newEngin.displayName;
                item.isAtelier = dataResult.newEngin.detailedResource;
                item.resourceNotAvailableForThisVersion = false;

                this.operationService.updateVehicle(operation.id!, originalVehicleId, item).subscribe({
                  next: (updatedOperation) => this.refetchAndUpdateOperation(operation.id!, this.translate.instant('success_vehicle_details_updated')),
                  error: (err) => this.handleApiError(err)
                });
              }
              this.isLoadingProducts = false;
            });
          },
          (err) => {
            this.handleApiError(err);
            this.isLoadingProducts = false;
          }
        );
      },
      (err) => {
        this.handleApiError(err);
        this.isLoadingProducts = false;
      }
    );
  }

  editVehicleDetails(item: Vehicle, operation: Operation): void {
    if (!this.isEditable || !operation.id) return;

    const quantityToSendAsData = this.displayAsDay ? item.quantity : this.getHoursFromDay(item.quantity);

    const dialogRef = this.dialog.open(DialogEditEnginComponent, {
      data: {
        quantity: quantityToSendAsData,
        displayName: item.displayName,
        displayAsDay: this.displayAsDay
      }
    });

    dialogRef.afterClosed().subscribe((result: { quantity: number }) => {
      if (result && result.quantity !== undefined) {
        const newQuantityInDays = this.displayAsDay ? result.quantity : this.getDayFromHours(result.quantity);

        if (item.quantity === newQuantityInDays) return;

        item.quantity = newQuantityInDays;
        this.spinnerService.show();

        this.operationService.updateVehicle(operation.id!, item.vehicleId!, item).subscribe({
          next: () => {
            this.refetchAndUpdateOperation(operation.id!, this.translate.instant('success_vehicle_details_updated'));
          },
          error: (err) => this.handleApiError(err)
        });
      }
    });
  }

  editDredgingEquipment(item: DredgingProjectUsageResourceModel, operation: Operation): void {
    if (!this.isEditable || !operation.id) return;

    const dialogRef = this.dialog.open(DialogDredgingEquipmentComponent, {
      maxHeight: '80vh',
      panelClass: 'dredging-equipment-dialog',
      data: { ...item }
    });

    dialogRef.afterClosed().subscribe((result: DialogDredgingEquipmentComponentResponseDataModel) => {
      if (!result) return;
      Object.assign(item, result.dredge);
      this.persistOperation(operation, this.translate.instant('success_dredging_equipment_updated'), result.selectedFile, result.dredge.id, 'dredge');
    });
  }

  editTunnelingEquipment(item: TunnelingProjectUsageResourceModel, operation: Operation): void {
    if (!this.isEditable || !operation.id) return;

    const dialogRef = this.dialog.open(DialogTunnelingEquipmentComponent, {
      maxHeight: '90vh',
      panelClass: 'tunneling-equipment-dialog',
      data: { tunneler: { ...item }, versionId: this.versionId }
    });

    dialogRef.afterClosed().subscribe((result: DialogTunnelingEquipmentComponentResponseDataModel) => {
      if (!result) return;
      Object.assign(item, result.tunneler);
      item.energyName = this.resolveTunnelerEnergyName(result.tunneler.selectedFuel);
      this.persistOperation(operation, this.translate.instant('success_tunneling_equipment_updated'), result.selectedFile, result.tunneler.id, 'tunnel');
    });
  }

  uploadDredgeOrTunnelProof(item: DredgingProjectUsageResourceModel | TunnelingProjectUsageResourceModel, operation: Operation, kind: 'dredge' | 'tunnel', event: Event): void {
    if (!this.isEditable) return;
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (!item.id) {
      this.snackbarService.showMessage(this.translate.instant('error_save_before_uploading_proof'));
      return;
    }
    this.spinnerService.show();
    const formData = new FormData();
    formData.append('file', file);
    const upload$ = kind === 'dredge' ? this.uploadService.uploadDredgeProof(item.id, formData) : this.uploadService.uploadTunnelingProof(item.id, formData);
    upload$.subscribe({
      next: () => {
        this.snackbarService.open(SnackbarTypes.update, this.translate.instant('success_justificative_added'));
        this.refetchAndUpdateOperation(operation.id!);
      },
      error: (err) => this.handleApiError(err)
    });
  }

  editSyntheticProof(item: OperationProofItem, operation: Operation): void {
    if (!item.syntheticItem) return;
    if (item.syntheticKind === 'dredge') {
      this.editDredgingEquipment(item.syntheticItem as DredgingProjectUsageResourceModel, operation);
      return;
    }
    if (item.syntheticKind === 'tunnel') {
      this.editTunnelingEquipment(item.syntheticItem as TunnelingProjectUsageResourceModel, operation);
    }
  }

  deleteSyntheticProof(item: OperationProofItem, operation: Operation): void {
    if (!this.isEditable || !item.syntheticItem) return;
    const target = item.syntheticItem;
    target.fileName = '';
    target.fileDescription = '';
    this.persistOperation(operation, this.translate.instant('success_justificative_deleted'));
  }

  getDredgeOrTunnelProofUrl(item: OperationProofItem): string {
    if (!item.syntheticItem || !item.id) return '';
    const storedFileName = item.syntheticItem.fileDescription;
    if (!storedFileName) return '';
    return `${environment.baseUrl}Documents/VehicleProof/${item.id}/${storedFileName}`;
  }

  deleteDredgingEquipment(item: DredgingProjectUsageResourceModel, operation: Operation): void {
    if (!this.isEditable || !operation.id || !operation.detailedOperation?.dredging) return;
    const idx = operation.detailedOperation.dredging.indexOf(item);
    if (idx < 0) return;
    operation.detailedOperation.dredging.splice(idx, 1);
    this.persistOperation(operation, this.translate.instant('success_engine_deleted'));
  }

  deleteTunnelingEquipment(item: TunnelingProjectUsageResourceModel, operation: Operation): void {
    if (!this.isEditable || !operation.id || !operation.detailedOperation?.tunneling) return;
    const idx = operation.detailedOperation.tunneling.indexOf(item);
    if (idx < 0) return;
    operation.detailedOperation.tunneling.splice(idx, 1);
    this.persistOperation(operation, this.translate.instant('success_engine_deleted'));
  }

  private resolveTunnelerEnergyName(selectedFuel?: string): string | undefined {
    if (!selectedFuel || selectedFuel === GlobalConstants.EMPTY_GUID) return this.defaultTunnelerFuelName();
    return (this.vehicleEnergies || []).find((e) => e.resourceId === selectedFuel)?.displayName;
  }

  defaultTunnelerFuelName(): string | undefined {
    const gnr = (this.vehicleEnergies || []).find((e) => e.code === 'GNR' && e.ruade);
    return gnr ? this.translate.instant('tunneling_default_fuel', { energy: gnr.displayName }) : undefined;
  }

  private persistOperation(operation: Operation, successMessage: string, selectedFile?: File | null, proofId?: string, proofKind?: 'dredge' | 'tunnel', onComplete?: () => void): void {
    if (!operation.id) {
      onComplete?.();
      return;
    }
    this.spinnerService.show();
    this.operationService.update(operation.id, operation).subscribe({
      next: () => {
        if (selectedFile && proofId && proofKind) {
          const formData = new FormData();
          formData.append('file', selectedFile);
          const upload$ = proofKind === 'dredge' ? this.uploadService.uploadDredgeProof(proofId, formData) : this.uploadService.uploadTunnelingProof(proofId, formData);
          upload$.subscribe({
            next: () => {},
            error: (err) => {
              this.handleApiError(err);
              onComplete?.();
            },
            complete: () => {
              this.refetchAndUpdateOperation(operation.id!, successMessage);
              onComplete?.();
            }
          });
          return;
        }
        this.refetchAndUpdateOperation(operation.id!, successMessage);
        onComplete?.();
      },
      error: (err) => {
        this.handleApiError(err);
        onComplete?.();
      }
    });
  }

  addProduct(type: 'input' | 'output', operation: Operation): void {
    if (!this.isEditable || !operation.id || this.isLoadingProducts) return;
    this.isLoadingProducts = true;

    if (type === 'output') {
      this.operationService.getProduitsSortants(operation.type).subscribe((data) => {
        const dialogRef = this.dialog.open(DialogAddCompositionComponent, {
          data: {
            fullTree: data
          }
        });

        dialogRef.afterClosed().subscribe((selectedProducts: any[]) => {
          if (selectedProducts && selectedProducts.length > 0) {
            this.spinnerService.show();
            const productsToAdd: ProductOperation[] = selectedProducts.map((p) => ({
              productId: p.resourceId,
              displayName: p.displayName,
              productCode: p.code,
              quantity: 0,
              workUnit: p.workUnit
            }));

            const addCalls = productsToAdd.map((p) => this.operationService.addOutboundProduct(operation.id!, p));
            forkJoin(addCalls).subscribe({
              next: () => {
                const message = selectedProducts.length > 1 ? this.translate.instant('success_products_added', { count: selectedProducts.length }) : this.translate.instant('success_product_added');
                this.refetchAndUpdateOperation(operation.id!, message);
              },
              error: (err) => this.handleApiError(err)
            });
          }
          this.isLoadingProducts = false;
        });
      });
    } else {
      const operationType = operation.type || undefined;
      forkJoin({
        data: this.productFormulaService.getAll(GroupBy.family, operationType, this.versionId),
        favoriteData: this.productFormulaService.getAll(GroupBy.favorite, operationType),
        fullDatabaseTree: this.productFormulaService.getAll(GroupBy.family, undefined, this.versionId)
      }).subscribe(({ data, favoriteData, fullDatabaseTree }) => {
        const dialogRef = this.dialog.open(DialogAddCompositionComponent, {
          data: { fullTree: data, favoriteData: favoriteData, fullDatabaseTree: fullDatabaseTree, filterEmptyParents: true }
        });

        dialogRef.afterClosed().subscribe((selectedProducts: any[]) => {
          if (selectedProducts && selectedProducts.length > 0) {
            this.spinnerService.show();
            const productsToAdd: ProductOperation[] = selectedProducts.map((p) => ({
              productId: p.resourceId,
              displayName: p.displayName,
              productCode: p.code,
              energyId: p.energyId,
              transportId: p.transportId,
              distance: 0,
              quantity: 0,
              workUnit: p.workUnit,
              pipeProduct: p.pipeProduct,
              cableProduct: p.cableProduct
            }));

            if (!operation.detailedOperation) {
              operation.detailedOperation = {};
            }
            if (!operation.detailedOperation.inputProducts) {
              operation.detailedOperation.inputProducts = [];
            }

            productsToAdd.forEach((p) => {
              operation.detailedOperation!.inputProducts!.push(p);
            });

            const detailedOperation = this.setDetailedOperation(operation);
            detailedOperation.versionId = this.versionId;

            this.projectService.CheckResourceForProjectVersion(detailedOperation).subscribe({
              next: (response) => {
                if (response.detailedOperation && response.detailedOperation.inputProducts) {
                  response.detailedOperation.inputProducts.forEach((updateProductOperation: ProductOperation) => {
                    if (updateProductOperation.productId) {
                      const productOperationToUpdate = operation.detailedOperation?.inputProducts?.find((productOperation) => productOperation.productId === updateProductOperation.productId);
                      if (productOperationToUpdate) {
                        productOperationToUpdate.resourceNotAvailableForThisVersion = updateProductOperation.resourceNotAvailableForThisVersion;
                      }
                    }
                  });
                }

                const addCalls = productsToAdd.map((p) => this.operationService.addInboundProduct(operation.id!, p));
                forkJoin(addCalls).subscribe({
                  next: () => {
                    const message = selectedProducts.length > 1 ? this.translate.instant('success_products_added', { count: selectedProducts.length }) : this.translate.instant('success_product_added');
                    this.refetchAndUpdateOperation(operation.id!, message);
                  },
                  error: (err) => this.handleApiError(err)
                });
              },
              error: (err) => {
                const addCalls = productsToAdd.map((p) => this.operationService.addInboundProduct(operation.id!, p));
                forkJoin(addCalls).subscribe({
                  next: () => {
                    const message = selectedProducts.length > 1 ? this.translate.instant('success_products_added', { count: selectedProducts.length }) : this.translate.instant('success_product_added');
                    this.refetchAndUpdateOperation(operation.id!, message);
                  },
                  error: (err) => this.handleApiError(err)
                });
              }
            });
          }
          this.isLoadingProducts = false;
        });
      });
    }
  }

  addVehicle(operation: Operation): void {
    if (!this.isEditable || !operation.id || this.isLoadingProducts) return;
    this.isLoadingProducts = true;
    this.spinnerService.show();

    forkJoin({
      allEngins: this.enginService.getAll(undefined, operation.type),
      favoriteEngins: this.enginService.getAll(GroupBy.favoriteDialog, operation.type),
      fullDatabaseEngins: this.enginService.getAll()
    }).subscribe({
      next: ({ allEngins, favoriteEngins, fullDatabaseEngins }) => {
        this.spinnerService.hide();

        if (operation.type === 'MaritimeWork') {
          const dredgeLabel = this.translate.instant('engin_category_maritime_dredge');
          (allEngins as any)[dredgeLabel] = {
            0: {
              code: 'DRA',
              detailedResource: false,
              displayName: dredgeLabel,
              index: 0,
              resourceType: ResourceType.Dredge,
              resourceId: GlobalConstants.EMPTY_GUID
            }
          };
        }
        if (operation.type === 'UndergroundWork') {
          const tunnelierLabel = this.translate.instant('common_field_tunneling');
          (allEngins as any)[tunnelierLabel] = {
            0: {
              code: 'TUN',
              detailedResource: false,
              displayName: tunnelierLabel,
              index: 0,
              resourceType: ResourceType.Tunnelier,
              resourceId: GlobalConstants.EMPTY_GUID
            }
          };
        }

        const dialogRef = this.dialog.open(DialogAddCompositionComponent, {
          data: {
            fullTree: allEngins,
            favoriteData: favoriteEngins,
            fullDatabaseTree: fullDatabaseEngins
          }
        });

        dialogRef.afterClosed().subscribe((selectedEngins: any[]) => {
          if (!selectedEngins || selectedEngins.length === 0) {
            this.isLoadingProducts = false;
            return;
          }

          const dredgesToAdd: DredgingProjectUsageResourceModel[] = [];
          const tunnelersToAdd: TunnelingProjectUsageResourceModel[] = [];
          const vehiclesToAdd: Vehicle[] = [];

          selectedEngins.forEach((el: any) => {
            if (el.resourceType === ResourceType.Dredge) {
              dredgesToAdd.push({ displayName: el.displayName });
              return;
            }
            if (el.resourceType === ResourceType.Tunnelier) {
              tunnelersToAdd.push({ displayName: el.displayName });
              return;
            }
            vehiclesToAdd.push({
              displayName: el.displayName,
              vehicleId: el.resourceId,
              isAtelier: el.detailedResource,
              quantity: 1
            });
          });

          let pending = 0;
          const release = () => {
            if (--pending <= 0) this.isLoadingProducts = false;
          };

          if (dredgesToAdd.length > 0 || tunnelersToAdd.length > 0) {
            if (!operation.detailedOperation) operation.detailedOperation = {};
            if (dredgesToAdd.length > 0) {
              operation.detailedOperation.dredging = [...(operation.detailedOperation.dredging || []), ...dredgesToAdd];
            }
            if (tunnelersToAdd.length > 0) {
              operation.detailedOperation.tunneling = [...(operation.detailedOperation.tunneling || []), ...tunnelersToAdd];
            }
            pending++;
            this.persistOperation(operation, this.translate.instant('success_engine_added'), undefined, undefined, undefined, release);
          }

          if (vehiclesToAdd.length > 0) {
            pending++;
            this.spinnerService.show();
            const addCalls = vehiclesToAdd.map((v) => this.operationService.addVehicle(operation.id!, v));
            forkJoin(addCalls).subscribe({
              next: () => {
                this.snackbarService.open(SnackbarTypes.add, this.translate.instant('success_engine_added'));
                this.spinnerService.hide();
                this.refetchAndUpdateOperation(operation.id!);
                release();
              },
              error: (err) => {
                this.handleApiError(err);
                release();
              }
            });
          }

          if (pending === 0) this.isLoadingProducts = false;
        });
      },
      error: (err) => this.handleApiError(err)
    });
  }

  deleteProduct(item: ProductOperation, type: 'input' | 'output', operation: Operation): void {
    if (!this.isEditable || !operation.id || !item.operationCompositionId) return;
    if (item.sourceOperationCompositionId) {
      this.snackbarService.showMessage(this.translate.instant('info_delete_initial_resource'));
      return;
    }

    this.spinnerService.show();

    const deleteCall = type === 'input' ? this.operationService.deleteInboundProduct(operation.id, item.operationCompositionId) : this.operationService.deleteOutboundProduct(operation.id, item.operationCompositionId);

    deleteCall.subscribe({
      next: () => {
        this.snackbarService.open(SnackbarTypes.delete);
        this.spinnerService.hide();
        this.refetchAndUpdateOperation(operation.id!);
      },
      error: (err) => this.handleApiError(err)
    });
  }

  deleteVehicle(item: Vehicle, operation: Operation): void {
    if (!this.isEditable || !operation.id || !item.materialCompositionId) return;
    if (item.sourceMaterialCompositionId) {
      this.snackbarService.showMessage(this.translate.instant('info_delete_initial_resource'));
      return;
    }

    this.spinnerService.show();

    this.operationService.deleteVehicle(operation.id, item.materialCompositionId).subscribe({
      next: () => {
        this.snackbarService.open(SnackbarTypes.delete);
        this.spinnerService.hide();
        this.refetchAndUpdateOperation(operation.id!);
      },
      error: (err) => this.handleApiError(err)
    });
  }

  addProof(item: ProductOperation | Vehicle, operation: Operation): void {
    if (!this.isEditable || !operation.id) return;

    const dialogRef = this.dialog.open(DialogJustificativeComponent, {
      width: '600px',
      data: {
        operationProofs: operation.operationProofs || [],
        resourceName: item.displayName,
        resourceType: this.getResourceType(item, operation)
      }
    });

    dialogRef.afterClosed().subscribe((result: OperationProofWithFile) => {
      if (!result || !operation.id) return;

      this.spinnerService.show();

      const currentResourceCompositionId = 'productId' in item ? (item as ProductOperation).operationCompositionId : (item as Vehicle).materialCompositionId;

      if (!currentResourceCompositionId) {
        this.handleApiError('Could not determine the resource ID to link.');
        return;
      }

      if (result.id) {
        const proofToUpdate = { ...result };

        if (operation.detailedOperation?.inputProducts?.some((p) => p.operationCompositionId === currentResourceCompositionId)) {
          proofToUpdate.inputProductProoved = [...new Set([...(proofToUpdate.inputProductProoved || []), currentResourceCompositionId])];
        } else if (operation.detailedOperation?.outputProducts?.some((p) => p.operationCompositionId === currentResourceCompositionId)) {
          proofToUpdate.outputProductProoved = [...new Set([...(proofToUpdate.outputProductProoved || []), currentResourceCompositionId])];
        } else {
          proofToUpdate.vehicleProoved = [...new Set([...(proofToUpdate.vehicleProoved || []), currentResourceCompositionId])];
        }

        this.operationService.updateProof(operation.id, proofToUpdate.id!, proofToUpdate).subscribe({
          next: () => this.refetchAndUpdateOperation(operation.id!, this.translate.instant('success_justificative_updated')),
          error: (err) => this.handleApiError(err)
        });
      } else {
        const proofToAdd = result;
        if (operation.detailedOperation?.inputProducts?.some((p) => p.operationCompositionId === currentResourceCompositionId)) {
          proofToAdd.inputProductProoved = [currentResourceCompositionId];
        } else if (operation.detailedOperation?.outputProducts?.some((p) => p.operationCompositionId === currentResourceCompositionId)) {
          proofToAdd.outputProductProoved = [currentResourceCompositionId];
        } else {
          proofToAdd.vehicleProoved = [currentResourceCompositionId];
        }

        this.operationService.addProof(operation.id, proofToAdd).subscribe({
          next: async (createdProof: OperationProof) => {
            if (createdProof.id && (result as any).fileToUpload) {
              const formData = new FormData();
              const file = (result as any).fileToUpload;
              formData.append('file', file, `${createdProof.id}.${file.name.split('.').pop()}`);
              this.uploadService
                .uploadWorkInspectionProof(createdProof.id, formData)
                .toPromise()
                .then(() => this.refetchAndUpdateOperation(operation.id!, this.translate.instant('success_justificative_added')))
                .catch((err) => this.discardProofsWithoutFile(operation.id!, [createdProof.id!], err));
            } else this.refetchAndUpdateOperation(operation.id!, this.translate.instant('success_justificative_added'));
          },
          error: (err) => this.handleApiError(err)
        });
      }
    });
  }

  addMultipleProofs(operation: Operation): void {
    if (!this.isEditable || !operation.id) return;

    const dialogRef = this.dialog.open(DialogUploadJustificativesComponent, {
      width: '750px',
      data: {
        operation: operation
      }
    });

    dialogRef.afterClosed().subscribe((result: OperationProofWithFile[]) => {
      if (!result || result.length === 0 || !operation.id) return;

      this.spinnerService.show();

      const proofsToCreate = result.map((proof) => {
        const { fileToUpload, ...proofWithoutFile } = proof as any;
        return proofWithoutFile;
      });

      this.operationService.addBatchProofs(operation.id, proofsToCreate).subscribe({
        next: (createdProofs: any[]) => {
          const uploadObservables: Observable<any>[] = [];
          const uploadedProofIds: string[] = [];

          createdProofs.forEach((createdProof) => {
            const matchingFile = result.find((r) => r.fileName === createdProof.fileName);

            if (matchingFile && (matchingFile as any).fileToUpload && createdProof.id) {
              const formData = new FormData();
              const file = (matchingFile as any).fileToUpload;
              formData.append('file', file, `${createdProof.id}.${file.name.split('.').pop()}`);

              uploadObservables.push(this.uploadService.uploadWorkInspectionProof(createdProof.id, formData));
              uploadedProofIds.push(createdProof.id);
            }
          });

          if (uploadObservables.length === 0) {
            this.snackbarService.showMessage(this.translate.instant('success_justificatives_added', { count: result.length }));
            this.spinnerService.hide();
            this.refetchAndUpdateOperation(operation.id!);
            return;
          }

          forkJoin(uploadObservables).subscribe({
            next: () => {
              this.snackbarService.showMessage(this.translate.instant('success_justificatives_added', { count: result.length }));
              this.spinnerService.hide();
              this.refetchAndUpdateOperation(operation.id!);
            },
            error: (err) => this.discardProofsWithoutFile(operation.id!, uploadedProofIds, err)
          });
        },
        error: (err) => this.handleApiError(err)
      });
    });
  }

  private discardProofsWithoutFile(operationId: string, proofIds: string[], error: any): void {
    forkJoin(proofIds.map((proofId) => this.operationService.deleteProof(operationId, proofId).pipe(catchError(() => of(null))))).subscribe(() => {
      this.handleApiError(error);
      this.refetchAndUpdateOperation(operationId);
    });
  }

  editProof(item: OperationProofItem, operation: Operation): void {
    if (!this.isEditable || !operation.id || !item.id) return;

    const dialogRef = this.dialog.open(DialogEditProofComponent, {
      width: '600px',
      data: {
        proof: item,
        operation: operation
      }
    });

    dialogRef.afterClosed().subscribe((result: OperationProof) => {
      if (result && operation.id && item.id) {
        this.spinnerService.show();

        this.operationService.updateProof(operation.id, item.id, result).subscribe({
          next: (updatedOperation) => {
            this.loadProofsForOperation(updatedOperation);
            this.snackbarService.showMessage(this.translate.instant('success_justificative_modified'));
            this.spinnerService.hide();
            this.refetchAndUpdateOperation(operation.id!);
          },
          error: (err) => this.handleApiError(err)
        });
      }
    });
  }

  deleteProof(item: OperationProofItem, operation: Operation): void {
    if (!this.isEditable || !operation.id || !item.id) return;

    this.spinnerService.show();

    this.operationService.deleteProof(operation.id, item.id).subscribe({
      next: () => {
        this.snackbarService.showMessage(this.translate.instant('success_justificative_deleted'));
        this.spinnerService.hide();
        this.refetchAndUpdateOperation(operation.id!);
      },
      error: (err) => this.handleApiError(err)
    });
  }

  private getResourceType(item: ProductOperation | Vehicle, operation: Operation): 'input' | 'output' | 'vehicle' {
    if ('productId' in item) {
      if (operation.detailedOperation?.inputProducts?.includes(item as ProductOperation)) {
        return 'input';
      }
      return 'output';
    }
    return 'vehicle';
  }

  hasProofs(item: ProductOperation | Vehicle, operation: Operation): boolean {
    if (!operation.operationProofs || operation.operationProofs.length === 0) {
      return false;
    }

    if ('productId' in item) {
      return operation.operationProofs.some((proof) => proof.inputProductProoved?.includes(item.productId!) || proof.outputProductProoved?.includes(item.productId!));
    } else {
      return operation.operationProofs.some((proof) => proof.vehicleProoved?.includes((item as Vehicle).vehicleId!));
    }
  }

  loadProofsForOperation(operation: Operation): void {
    this.unfilteredProofs = this.getProofsForOperation(operation);
    this.filteredProofs = [...this.unfilteredProofs];
    this.updateProofRows();

    const resourceNames = new Set<string>();
    const proofTypes = new Set<string>();

    operation.operationProofs?.forEach((proof) => {
      proof.inputProductProoved?.forEach((compositionId) => {
        const product = operation.detailedOperation?.inputProducts?.find((p) => p.operationCompositionId === compositionId);
        if (product?.displayName) {
          resourceNames.add(product.displayName);
        }
      });
      proof.outputProductProoved?.forEach((compositionId) => {
        const product = operation.detailedOperation?.outputProducts?.find((p) => p.operationCompositionId === compositionId);
        if (product?.displayName) {
          resourceNames.add(product.displayName);
        }
      });
      proof.vehicleProoved?.forEach((compositionId) => {
        const vehicle = operation.detailedOperation?.vehicles?.find((v) => v.materialCompositionId === compositionId);
        if (vehicle?.displayName) {
          resourceNames.add(vehicle.displayName);
        }
      });
      proof.proovedTypes?.forEach((type) => {
        proofTypes.add(this.getProofTypeDisplay(type));
      });
    });

    this.availableResources = Array.from(resourceNames).sort();
    this.availableProofTypes = Array.from(proofTypes).sort();

    this.proofFilterForm.reset(
      {
        fileNameSearch: '',
        resourceNameFilter: '',
        typesFilter: []
      },
      { emitEvent: false }
    );
  }

  getProofsForOperation(operation: Operation): OperationProofItem[] {
    const baseProofs: OperationProofItem[] = (operation.operationProofs || []).map((proof) => {
      const resourceNames: string[] = [];

      proof.inputProductProoved?.forEach((compositionId) => {
        const product = operation.detailedOperation?.inputProducts?.find((p) => p.operationCompositionId === compositionId);
        if (product) {
          resourceNames.push(product.displayName || this.translate.instant('input_product'));
        }
      });

      proof.outputProductProoved?.forEach((compositionId) => {
        const product = operation.detailedOperation?.outputProducts?.find((p) => p.operationCompositionId === compositionId);
        if (product) {
          resourceNames.push(product.displayName || this.translate.instant('output_product'));
        }
      });

      proof.vehicleProoved?.forEach((compositionId) => {
        const vehicle = operation.detailedOperation?.vehicles?.find((v) => v.materialCompositionId === compositionId);
        if (vehicle) {
          resourceNames.push(vehicle.displayName || this.translate.instant('vehicle'));
        }
      });

      const proofItem: OperationProofItem = {
        ...proof,
        resourceName: resourceNames.length > 0 ? resourceNames.join(', ') : this.translate.instant('unspecified_resource')
      };

      return proofItem;
    });

    const dredgeProofs: OperationProofItem[] = (operation.detailedOperation?.dredging || [])
      .filter((d) => !!d.fileName)
      .map((d) => ({
        id: d.id,
        fileName: d.fileDescription || d.fileName,
        resourceName: d.displayName || this.translate.instant('unspecified_resource'),
        syntheticKind: 'dredge',
        syntheticItem: d
      }));

    const tunnelProofs: OperationProofItem[] = (operation.detailedOperation?.tunneling || [])
      .filter((t) => !!t.fileName)
      .map((t) => ({
        id: t.id,
        fileName: t.fileName,
        resourceName: t.displayName || this.translate.instant('unspecified_resource'),
        syntheticKind: 'tunnel',
        syntheticItem: t
      }));

    return [...baseProofs, ...dredgeProofs, ...tunnelProofs];
  }

  private getProofTypeDisplay(type: EOperationProofType): string {
    return ProofTypeLabelKeys[type] ? this.translate.instant(ProofTypeLabelKeys[type]) : this.translate.instant('unknown');
  }

  getProofTypesDisplay(types?: EOperationProofType[]): string {
    if (!types || types.length === 0) {
      return '';
    }
    return types.map((type) => this.getProofTypeDisplay(type)).join(', ');
  }

  applyFilters(): void {
    const filters = this.proofFilterForm.value;

    this.filteredProofs = this.unfilteredProofs.filter((proof) => {
      const fileNameMatch = filters.fileNameSearch ? proof.fileName?.toLowerCase().includes(filters.fileNameSearch.toLowerCase()) : true;

      const resourceNameMatch = filters.resourceNameFilter ? proof.resourceName.includes(filters.resourceNameFilter) : true;

      const typesMatch = filters.typesFilter && filters.typesFilter.length > 0 ? filters.typesFilter.every((type: string) => proof.proovedTypes?.some((pt) => this.getProofTypeDisplay(pt) === type)) : true;

      return fileNameMatch && resourceNameMatch && typesMatch;
    });
    this.updateProofRows();
  }

  private refreshResourceRows(): void {
    this.inputProductRows = this.buildProductRows('input');
    this.outputProductRows = this.buildProductRows('output');
    this.vehicleRows = this.buildVehicleRows();
    this.updateProofRows();
  }

  private buildProductRows(type: 'input' | 'output'): TableRow[] {
    const products = type === 'input' ? this._operation?.detailedOperation?.inputProducts : this._operation?.detailedOperation?.outputProducts;
    return (products || []).map((item, index) => ({
      id: item.operationCompositionId || `${type}-${index}`,
      item,
      type
    }));
  }

  private buildVehicleRows(): TableRow[] {
    const vehicles = this._operation?.detailedOperation?.vehicles || [];
    const dredging = this._operation?.detailedOperation?.dredging || [];
    const tunneling = this._operation?.detailedOperation?.tunneling || [];

    const vehicleRows: TableRow[] = vehicles.map((vehicle, index) => ({
      id: vehicle.materialCompositionId || `vehicle-${index}`,
      item: vehicle,
      kind: 'vehicle'
    }));
    const dredgeRows: TableRow[] = dredging.map((item, index) => ({
      id: item.id || `dredge-${index}`,
      item,
      kind: 'dredge'
    }));
    const tunnelRows: TableRow[] = tunneling.map((item, index) => ({
      id: item.id || `tunnel-${index}`,
      item,
      kind: 'tunnel'
    }));

    return [...vehicleRows, ...dredgeRows, ...tunnelRows];
  }

  private updateProofRows(): void {
    this.proofRows = (this.filteredProofs || []).map((proof, index) => ({
      id: proof.id || `proof-${index}`,
      resourceName: proof.resourceName,
      fileName: proof.fileName,
      typesDisplay: this.getProofTypesDisplay(proof.proovedTypes),
      proof
    }));
  }

  private handleApiError(error: any): void {
    console.error('API Error:', error);
    this.snackbarService.open(SnackbarTypes.error);
    this.spinnerService.hide();
  }

  getProofCountForItem(item: ProductOperation | Vehicle, operation: Operation): number {
    if (!operation.operationProofs || operation.operationProofs.length === 0) {
      return 0;
    }

    let proofCount = 0;
    if ('productId' in item) {
      const productItem = item as ProductOperation;
      proofCount = operation.operationProofs.filter((proof) => proof.inputProductProoved?.includes(productItem.operationCompositionId!) || proof.outputProductProoved?.includes(productItem.operationCompositionId!)).length;
    } else {
      const vehicleItem = item as Vehicle;
      proofCount = operation.operationProofs.filter((proof) => proof.vehicleProoved?.includes(vehicleItem.materialCompositionId!)).length;
    }
    return proofCount;
  }

  toggleCommentExpansion(proofId?: string): void {
    if (!proofId) return;

    if (this.expandedComments.has(proofId)) {
      this.expandedComments.delete(proofId);
    } else {
      this.expandedComments.add(proofId);
    }
  }

  isCommentExpanded(proofId?: string): boolean {
    if (!proofId) return false;
    return this.expandedComments.has(proofId);
  }

  onDragStart(event: DragEvent): void {
    if (!this.operation.id) return;

    event.dataTransfer?.setData('text/plain', this.operation.id);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
    this.isDragging = true;
  }

  onDragEnd(): void {
    this.isDragging = false;
  }

  removeMultimodalTransport(item: ProductOperation, operation: Operation, type: 'input' | 'output'): void {
    if (!this.isEditable || !operation.id) return;

    item.transportId = undefined;
    item.transportName = undefined;
    item.isMultimodal = false;
    item.distance = 0;

    this.spinnerService.show();

    const updateCall$ = type === 'input' ? this.operationService.updateInboundProduct(operation.id!, item.productId!, item) : this.operationService.updateOutboundProduct(operation.id!, item.productId!, item);

    updateCall$.subscribe({
      next: () => {
        this.refetchAndUpdateOperation(operation.id!, this.translate.instant('success_multimodal_transport_removed'));
      },
      error: (err) => this.handleApiError(err)
    });
  }

  addOrEditMultimodalTransport(item: ProductOperation, operation: Operation, type: 'input' | 'output'): void {
    if (!this.isEditable || !operation.id) return;

    this.acheminementService.getAll().subscribe(
      (fullTree) => {
        this.acheminementService.getAll(GroupBy.favoriteDialog).subscribe(
          (favoriteData) => {
            const dialogRef = this.dialog.open(DialogAddCompositionComponent, {
              data: {
                fullTree: fullTree,
                favoriteData: favoriteData,
                isSingular: true
              }
            });

            dialogRef.afterClosed().subscribe((selectedRoutes: any[]) => {
              if (selectedRoutes && selectedRoutes.length > 0) {
                const selectedRoute = selectedRoutes[0];

                item.transportId = selectedRoute.resourceId;
                item.transportName = selectedRoute.displayName;
                item.isMultimodal = true;

                item.distance = undefined;
                item.energyId = undefined;
                item.energyName = undefined;
                item.isDoubleFreight = false;

                this.spinnerService.show();

                const updateCall$ = type === 'input' ? this.operationService.updateInboundProduct(operation.id!, item.productId!, item) : this.operationService.updateOutboundProduct(operation.id!, item.productId!, item);

                updateCall$.subscribe({
                  next: () => {
                    this.refetchAndUpdateOperation(operation.id!, this.translate.instant('success_multimodal_transport_applied'));
                  },
                  error: (err) => this.handleApiError(err)
                });
              }
            });
          },
          (err) => this.handleApiError(err)
        );
      },
      (err) => this.handleApiError(err)
    );
  }

  getHoursFromDay(days?: number): number {
    if (this.hoursForDay && days) {
      const hours = days * this.hoursForDay;
      return Math.round(hours * 100) / 100;
    }
    return 0;
  }

  getDayFromHours(hours?: number): number {
    if (this.hoursForDay && this.hoursForDay > 0 && hours) {
      const days = hours / this.hoursForDay;
      return Math.round(days * 100) / 100;
    }
    return 0;
  }

  getDredgeQuantityDisplay(weeks?: number): number {
    if (!weeks) return 0;
    const days = weeks * 7;
    if (this.displayAsDay) return Math.round(days * 100) / 100;
    return this.getHoursFromDay(days);
  }

  toggleProductPanel(productId?: string): void {
    if (!productId) return;
    const currentState = this.isProductPanelExpanded(productId);
    this.productPanelStates.set(productId, !currentState);
  }

  isProductPanelExpanded(productId?: string): boolean {
    if (!productId) return false;
    return this.productPanelStates.get(productId) || false;
  }

  toggleVehiclePanel(vehicleId?: string): void {
    if (!vehicleId) return;
    const currentState = this.isVehiclePanelExpanded(vehicleId);
    this.vehiclePanelStates.set(vehicleId, !currentState);
  }

  isVehiclePanelExpanded(vehicleId?: string): boolean {
    if (!vehicleId) return false;
    return this.vehiclePanelStates.get(vehicleId) || false;
  }

  updateProductField(field: Partial<ProductOperation>, item: ProductOperation, operation: Operation, type: 'input' | 'output'): void {
    if (!this.isEditable || !operation.id) return;

    Object.assign(item, field);
    this.spinnerService.show();

    const updateCall$ = type === 'input' ? this.operationService.updateInboundProduct(operation.id, item.productId!, item) : this.operationService.updateOutboundProduct(operation.id, item.productId!, item);

    updateCall$.subscribe({
      next: () => this.refetchAndUpdateOperation(operation.id!, this.translate.instant('success_product_updated')),
      error: (err) => this.handleApiError(err)
    });
  }

  updateVehicleField(field: Partial<Vehicle>, item: Vehicle, operation: Operation): void {
    if (!this.isEditable || !operation.id || !item.vehicleId) return;

    Object.assign(item, field);
    this.spinnerService.show();

    this.operationService.updateVehicle(operation.id, item.vehicleId, item).subscribe({
      next: () => this.refetchAndUpdateOperation(operation.id!, this.translate.instant('success_vehicle_details_updated')),
      error: (err) => this.handleApiError(err)
    });
  }

  updateOperationDescription(): void {
    if (!this.isEditable || !this.operation.id) return;

    this.spinnerService.show();
    this.operationService.update(this.operation.id, this.operation).subscribe({
      next: () => this.refetchAndUpdateOperation(this.operation.id!, this.translate.instant('success_description_updated')),
      error: (err) => this.handleApiError(err)
    });
  }

  getInputValue(event: Event): number {
    const target = event.target as HTMLInputElement | null;
    const value = target?.value || '0';
    return +value;
  }

  toggleDoubleFreight(item: ProductOperation, operation: Operation, type: 'input' | 'output'): void {
    if (!this.isEditable || !operation.id) return;

    item.isDoubleFreight = !item.isDoubleFreight;
    this.updateProductField({}, item, operation, type);
  }

  registerFieldForClose(field: any): void {
    if (field && typeof field.close === 'function') {
      field.close();
    }
  }

  getProofFileUrl(proof: OperationProofItem): string {
    if (!proof.id || !proof.fileName) {
      return '';
    }

    const baseUrl = environment.baseUrl + 'Documents/OperationProofs/';
    const fileExtension = proof.fileName.split('.').pop();
    const fileName = `${proof.id}.${fileExtension}`;
    return `${baseUrl}${proof.id}/${fileName}`;
  }

  private setDetailedOperation(operation: Operation): DetailedOperation {
    const detailedOperation: DetailedOperation = {};
    const productsIn: ProductInOut[] = [];
    const productsOut: ProductInOut[] = [];
    const engins: EnginOperation[] = [];

    operation.detailedOperation?.inputProducts?.forEach((product) => {
      productsIn.push({
        distance: product.distance,
        doubleFret: product.isDoubleFreight ? 1 : 0,
        energyId: product.energyId,
        quantity: product.quantity,
        resourceId: product.productId,
        transportId: product.transportId,
        workUnit: product.workUnit,
        pipeProduct: product.pipeProduct,
        diameter: product.diameter,
        unitWeight: product.unitWeight,
        productionSite: product.productionSite,
        cableProduct: product.cableProduct,
        section: product.section,
        nbConductors: product.nbConductors
      });
    });

    operation.detailedOperation?.outputProducts?.forEach((product) => {
      productsOut.push({
        distance: product.distance,
        doubleFret: product.isDoubleFreight ? 1 : 0,
        energyId: product.energyId,
        quantity: product.quantity,
        resourceId: product.productId,
        transportId: product.transportId,
        workUnit: product.workUnit,
        pipeProduct: product.pipeProduct,
        diameter: product.diameter,
        unitWeight: product.unitWeight,
        productionSite: product.productionSite,
        cableProduct: product.cableProduct,
        section: product.section,
        nbConductors: product.nbConductors
      });
    });

    operation.detailedOperation?.vehicles?.forEach((vehicle: Vehicle) => {
      engins.push({
        resourceId: vehicle.vehicleId,
        nbDays: vehicle.quantity
      });
    });

    detailedOperation.engins = engins;
    detailedOperation.productsIn = productsIn;
    detailedOperation.productsOut = productsOut;

    return detailedOperation;
  }
}
